import { COMPANY, GRIEVANCE_OFFICER } from '../company';
import type { LegalDoc } from './types';

/**
 * Terms of use, numbered like zenquip.com's, written for Nurture. Needs a legal read before
 * production (PROGRESS W3).
 */
export const TERMS: LegalDoc = {
  title: 'Terms of use',
  summary:
    'Nurture helps you understand your child’s everyday development and plan support at home. It is not a diagnosis. These terms explain what you can expect from us and what we ask of you.',
  updated: '28 September 2026',
  sections: [
    {
      id: 'acceptance',
      heading: 'Acceptance',
      blocks: [
        `These terms are an agreement between you and **${COMPANY.legalName}** (“ZenQuip”, “we”). By creating an account or using Nurture you accept them and our [privacy policy](/privacy). If you do not agree, please do not use Nurture.`,
      ],
    },
    {
      id: 'eligibility',
      heading: 'Who can use Nurture',
      blocks: [
        'You must be 18 or older and the parent or legal guardian of any child whose information you enter. Family members you invite may view reports; they cannot add children or change anything.',
        'Nurture is prepared for children from 18 months to 18 years.',
      ],
    },
    {
      id: 'service',
      heading: 'What Nurture is',
      blocks: [
        'You answer questions about your child’s everyday behaviour. Nurture prepares a report that explains, in plain words, how each area looks, Meeting milestones, Keep monitoring or Needs attention, with a page for your child’s doctor or therapist and, with a paid plan, a home programme of activities.',
        'Questions and content are built with clinicians on established developmental frameworks. The report reflects only what you told us, on the day you told us.',
      ],
    },
    {
      id: 'medical-disclaimer',
      heading: 'Medical disclaimer',
      blocks: [
        '**ZenQuip is a mental health support platform. It does not provide medical diagnoses and is not a substitute for evaluation or treatment by a qualified professional. If you are in crisis, call Tele-MANAS 14416 (or 1-800-891-4416) or KIRAN 1800-599-0019 or Vandrevala Foundation +91 9999 666 555 (India), or contact your local emergency services immediately.**',
        'A Nurture report is not a diagnosis, a screening result or a clinical assessment, and it does not replace advice from a qualified professional. Do not delay or stop professional care because of anything in a report. Home activities are general suggestions; stop any activity that distresses your child and ask a professional.',
        'Nurture is not an emergency service and is not monitored in real time.',
      ],
    },
    {
      id: 'how-results-are-made',
      heading: 'How results are prepared',
      blocks: [
        'Results come from fixed rules and clinician-written content: the same answers give the same result. No AI model writes your child’s results. Accuracy depends on your answers, so answer for your child’s usual behaviour.',
        'If you enter results from an earlier professional assessment, we use them as you enter them; please enter them exactly as written in that report.',
      ],
    },
    {
      id: 'your-responsibilities',
      heading: 'Your responsibilities',
      blocks: [
        [
          'give accurate information and keep it up to date;',
          'keep your sign-in private and tell us if you think someone else has used your account;',
          'add other people’s details (care team, family, nominee) only with good reason and in a way they would expect;',
          'share reports and links only with people you trust with your child’s information.',
        ],
      ],
    },
    {
      id: 'payments',
      heading: 'Payments and plans',
      blocks: [
        'The home assessment and the first pages of every report are free. What a paid unlock or Nurture Plus includes, and its price, with any GST shown separately or included, is shown before you pay. Nurture Plus renews each quarter until you cancel it in Settings; cancelling stops the next charge and you keep what you have paid for until the end of the quarter.',
        'Payments are processed by our payment provider. We may change prices for future purchases; a report you have paid for stays available as it was prepared.',
      ],
    },
    {
      id: 'refunds',
      heading: 'Refunds',
      blocks: ['See our [refund policy](/refunds).'],
    },
    {
      id: 'intellectual-property',
      heading: 'Intellectual property',
      blocks: [
        'Nurture’s questions, content, design and software belong to ZenQuip or its licensors. Your report is yours to keep, print and share for your child’s care. Please do not copy, resell or publish Nurture’s content, or use it to build a competing product.',
      ],
    },
    {
      id: 'prohibited-use',
      heading: 'What you must not do',
      blocks: [
        [
          'enter information about a child you are not the parent or legal guardian of;',
          'use Nurture to diagnose, label or make decisions about a child without a qualified professional;',
          'try to access other people’s accounts, reports or links, or disrupt the service;',
          'scrape, copy or reverse-engineer Nurture.',
        ],
      ],
    },
    {
      id: 'suspension',
      heading: 'Suspension',
      blocks: [
        'We may suspend or close an account that breaks these terms or puts others at risk. Where we can, we will tell you first and let you download your data.',
      ],
    },
    {
      id: 'liability',
      heading: 'Limitation of liability',
      blocks: [
        'Nurture is provided with care but “as is”. To the extent the law allows, ZenQuip is not liable for decisions made on the basis of a report, or for indirect or consequential loss. Our total liability to you for any claim is limited to what you paid us in the 12 months before it. Nothing here limits liability that cannot be limited by law.',
      ],
    },
    {
      id: 'indemnity',
      heading: 'Indemnity',
      blocks: [
        'If you break these terms or the law while using Nurture, and a claim is made against ZenQuip because of it, you agree to cover the reasonable costs of that claim.',
      ],
    },
    {
      id: 'privacy',
      heading: 'Privacy',
      blocks: ['How we handle personal data is explained in our [privacy policy](/privacy) and on our [Trust page](/trust).'],
    },
    {
      id: 'governing-law',
      heading: 'Governing law',
      blocks: [
        'These terms are governed by the laws of India. Subject to applicable law, the courts at **Jaipur, Rajasthan** have exclusive jurisdiction over disputes arising from them.',
      ],
    },
    {
      id: 'changes',
      heading: 'Changes to these terms',
      blocks: [
        'We may update these terms. We will post changes here and change the date above; if a change matters to you, we will tell you before it applies. Using Nurture after that means you accept the new terms.',
      ],
    },
    {
      id: 'contact',
      heading: 'Contact',
      blocks: [
        `Questions: [${COMPANY.supportEmail}](mailto:${COMPANY.supportEmail}). Complaints: our Grievance Officer, ${GRIEVANCE_OFFICER.name}, [${GRIEVANCE_OFFICER.email}](mailto:${GRIEVANCE_OFFICER.email}), ${GRIEVANCE_OFFICER.phone}.`,
      ],
    },
  ],
};
